import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, BookOpen, Users, Bell, Trash2, Eye, CheckCircle2, Inbox } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import ImageUpload from '@/components/ImageUpload';

const emptyForm = { name: '', author: '', publisher: '', series_type: 'manga', description: '', image_url: '' };
const typeBadge = { manga: 'bg-rose-100 text-rose-700 border-rose-200', bd: 'bg-amber-100 text-amber-800 border-amber-200' };

export function SeriesFormFields({ form, setForm }) {
  return (
    <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
      <div><Label className="text-xs font-medium text-slate-500">Nom de la série</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1 rounded-lg" data-testid="series-name-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs font-medium text-slate-500">Auteur</Label><Input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} className="mt-1 rounded-lg" data-testid="series-author-input" /></div>
        <div><Label className="text-xs font-medium text-slate-500">Éditeur</Label><Input value={form.publisher} onChange={e => setForm(f => ({ ...f, publisher: e.target.value }))} className="mt-1 rounded-lg" data-testid="series-publisher-input" /></div>
      </div>
      <div><Label className="text-xs font-medium text-slate-500">Type</Label>
        <Select value={form.series_type} onValueChange={v => setForm(f => ({ ...f, series_type: v }))}>
          <SelectTrigger className="mt-1 rounded-lg" data-testid="series-type-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="manga">Manga</SelectItem><SelectItem value="bd">BD</SelectItem></SelectContent>
        </Select>
      </div>
      <div><Label className="text-xs font-medium text-slate-500">Description</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1 rounded-lg" /></div>
      <ImageUpload value={form.image_url} onChange={v => setForm(f => ({ ...f, image_url: v }))} label="Couverture de la série" testId="series-image" />
    </div>
  );
}

export default function SeriesList() {
  const [series, setSeries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get('/admin/series').then(r => setSeries(r.data));
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    try {
      await api.post('/admin/series', form);
      toast.success("Série créée");
      setDialogOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleValidate = async (s) => {
    try { await api.put(`/admin/series/${s.id}`, { status: 'active' }); toast.success(`"${s.name}" est maintenant visible par vos clients`); load(); }
    catch { toast.error("Erreur lors de la validation"); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Supprimer "${s.name}" et ses ${s.follower_count} abonné(s) ?`)) return;
    try { await api.delete(`/admin/series/${s.id}`); toast.success("Série supprimée"); load(); }
    catch { toast.error("Erreur lors de la suppression"); }
  };

  const proposed = series.filter(s => s.status === 'proposee');
  const active = series.filter(s => s.status !== 'proposee');

  const Card = ({ s }) => (
    <div className={`bg-white border rounded-2xl p-5 hover:shadow-md transition-shadow ${s.status === 'proposee' ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'}`} data-testid={`admin-series-card-${s.id}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Badge className={`text-xs border ${typeBadge[s.series_type] || typeBadge.manga}`}>{s.series_type === 'bd' ? 'BD' : 'Manga'}</Badge>
        {s.status === 'proposee' && <Badge className="text-xs border bg-amber-100 text-amber-800 border-amber-200" data-testid={`proposed-badge-${s.id}`}>Proposée par un client</Badge>}
      </div>
      <h3 className="font-semibold text-slate-900 truncate">{s.name}</h3>
      <p className="text-sm text-slate-500 truncate">{[s.author, s.publisher].filter(Boolean).join(' · ') || (s.proposed_by ? `Demandée par ${s.proposed_by}` : '—')}</p>
      <div className="mt-3 flex gap-4 text-sm text-slate-500">
        <span className="flex items-center gap-1.5" data-testid={`admin-followers-${s.id}`}><Users className="h-3.5 w-3.5" />{s.follower_count} abonné{s.follower_count > 1 ? 's' : ''}</span>
        <span className="flex items-center gap-1.5"><Bell className="h-3.5 w-3.5" />{s.release_count} sortie{s.release_count > 1 ? 's' : ''}</span>
      </div>
      <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
        {s.status === 'proposee' && (
          <Button size="sm" className="rounded-full text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleValidate(s)} data-testid={`validate-series-${s.id}`}>
            <CheckCircle2 className="h-3 w-3 mr-1" /> Valider
          </Button>
        )}
        <Link to={`/admin/series/${s.id}`} className="flex-1">
          <Button variant="outline" size="sm" className="w-full rounded-full text-xs" data-testid={`manage-series-${s.id}`}><Eye className="h-3 w-3 mr-1" /> Gérer</Button>
        </Link>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500 hover:bg-rose-50" onClick={() => handleDelete(s)} data-testid={`delete-series-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-series-list">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Séries BD & Manga</h1>
          <p className="text-slate-500 mt-1">Vos clients suivent ces séries : annoncez-leur chaque nouvelle sortie</p>
        </div>
        <Button className="rounded-full" onClick={() => { setForm(emptyForm); setDialogOpen(true); }} data-testid="create-series-btn"><Plus className="h-4 w-4 mr-2" /> Nouvelle série</Button>
      </div>

      {proposed.length > 0 && (
        <section className="mb-10" data-testid="proposed-series-section">
          <div className="flex items-center gap-2 mb-4"><Inbox className="h-5 w-5 text-amber-500" /><h2 className="text-base md:text-lg font-bold text-slate-900">Demandes de clients ({proposed.length})</h2></div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{proposed.map(s => <Card key={s.id} s={s} />)}</div>
        </section>
      )}

      {active.length === 0 && proposed.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
          <BookOpen className="h-12 w-12 mx-auto text-slate-200" />
          <p className="mt-4 text-slate-500">Aucune série référencée</p>
          <Button className="mt-4 rounded-full" onClick={() => setDialogOpen(true)}>Créer une série</Button>
        </div>
      ) : (
        <section>
          {proposed.length > 0 && <h2 className="text-base md:text-lg font-bold text-slate-900 mb-4">Séries actives ({active.length})</h2>}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{active.map(s => <Card key={s.id} s={s} />)}</div>
        </section>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="series-dialog">
          <DialogHeader><DialogTitle>Nouvelle série</DialogTitle></DialogHeader>
          <SeriesFormFields form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Annuler</Button>
            <Button onClick={handleSave} className="rounded-full" data-testid="series-save-btn">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
